import * as React from 'react';
import { SagaIterator } from 'redux-saga';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import { Messages } from '../../../../codegen/Localize';
import { showActionableToast } from '../../../../common/react/components/ActionableMessage';
import { FavoriteRepresentation, FavoritesResource } from '../../../../favorites/resources/FavoritesResource';
import { getHomeAddFavoriteSucceededAction, getHomeRemoveFavoriteSucceededAction } from '../../../../home/actions/HomeActions';
import { FeatureFlags } from '../../../../ts/FeatureFlags';
import { L10n } from '../../../../ts/L10n/L10n';
import { ContentFiltersKeys } from '../../../../ts/models/ContentFilters';
import { IExplorableItemTypeInfo } from '../../../../ts/models/resourceInfoParts';
import { Favorites } from '../../../../ts/resources/Favorites';
import { IResult } from '../../../../ts/ServerApi/types/IResponse';
import { AddContentFavorite, RemoveContentFavorite } from '../../../actions/AppActionTypes';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { removeFromContentSelection } from '../../../actions/spaces/ContentExplorerSelectionActions';
import { addContentFavorite as addContentFavoriteAction, AddContentFavoriteAction, contentFavoriteAdded, contentFavoriteRemoved, RemoveContentFavoriteAction, removeFavoritedItemAction } from '../../../actions/spaces/FavoritesActions';
import { doesSpaceSupportStylishFiltering } from '../../../actions/spaces/filters/FilterHelpers';
import { appStore } from '../../../AppStore';
import { selectSpecificMixedContentFilterClause } from '../../../selectors/ContentFilterSelectors';
import { selectSpaceInfo } from '../../../selectors/ContentSelectors';
import { SessionUserSelectors } from '../../../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../../ServiceLocator';
import { isFavoritesSpace, isHomeSpace, isMetricFavoritesSubspace, isMetricSpace, SpaceInfo } from '../../../state/SpaceInfo';


export function* addContentFavorite(action: AddContentFavoriteAction) {
  try {
    yield call(addFavorite, action);
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    if(isHomeSpace(spaceInfo)) {
      yield put(getHomeAddFavoriteSucceededAction(action.contentId, action.contentType));
    } else {
      yield put(contentFavoriteAdded(action.contentId, action.contentType));
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.favoritesError()));
  }
}

export function addFavorite(action: AddContentFavoriteAction): ng.IPromise<IResult> {
  const { contentId, contentType } = action;
  if(FeatureFlags.crassFavorites && contentType as any === 'metric') {
    const favoritesResource = ServiceLocator.get<FavoritesResource>('FavoritesResource');
    const favorite: FavoriteRepresentation = {
      contentId: action.contentId,
      contentType: action.contentType,
    };

    const userId = SessionUserSelectors.sessionUserId(appStore.getState());
    return favoritesResource.add(favorite, userId);
  }
  else {
    const favoritesResource = ServiceLocator.get<Favorites>('Favorites');
    return favoritesResource.add(contentId, contentType);
  }
}

export function* removeContentFavorite(action: RemoveContentFavoriteAction) {
  try {
    yield call(removeFavorite, action);

    // we want to remove the item from state if we're in favorites space
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    const favoriteFilterApplied = yield select(selectSpecificMixedContentFilterClause, ContentFiltersKeys.favorite);
    const removeFavoriteFromExplorer = favoriteFilterApplied && doesSpaceSupportStylishFiltering(spaceInfo);

    if (isFavoritesSpace(spaceInfo) || isMetricFavoritesSubspace(spaceInfo) || removeFavoriteFromExplorer) {
      yield put(removeFavoritedItemAction(action.contentId, action.contentType));
      yield put(contentFavoriteRemoved(action.contentIdForFavoriting, action.contentType));

      const item: IExplorableItemTypeInfo = {id: action.contentId, contentType: action.contentType};
      yield put(removeFromContentSelection(item));
      showUndoRemoveFavoriteToast(action);
    } else if (isHomeSpace(spaceInfo)) {
      yield put(getHomeRemoveFavoriteSucceededAction(action.contentId, action.contentType));
      showUndoRemoveFavoriteToast(action);
    } else {
      yield put(contentFavoriteRemoved(action.contentIdForFavoriting, action.contentType));
    }
  } catch(e) {
    yield put(requestFailed(e, Messages.favoritesError()));
  }
}

export function removeFavorite(action: RemoveContentFavoriteAction): ng.IPromise<IResult> {
  const { contentIdForFavoriting, contentType } = action;

  if(FeatureFlags.crassFavorites && contentType as any === 'metric') {
    const favoritesResource = ServiceLocator.get<FavoritesResource>('FavoritesResource');
    const favorite: FavoriteRepresentation = {
      contentId: action.contentId,
      contentType: action.contentType,
    };

    const userId = SessionUserSelectors.sessionUserId(appStore.getState());
    return favoritesResource.remove(favorite, userId);
  }
  else {
    const favoritesResource = ServiceLocator.get<Favorites>('Favorites');
    return favoritesResource.remove(contentIdForFavoriting, contentType as any);
  }

}

export function* rootFavoritesSaga(): SagaIterator {
  yield takeEvery(AddContentFavorite, addContentFavorite);
  yield takeEvery(RemoveContentFavorite, removeContentFavorite);
}

const maxCharactersForName = 24;

function showUndoRemoveFavoriteToast(action: RemoveContentFavoriteAction) {
  showActionableToast({
    testId: 'undo-favorite',
    message: Messages.favoriteRemoved({NAME: L10n.elideIfLong(action.contentName, maxCharactersForName)}),
    actionMessage: Messages.favoriteRemovedUndo(),
    action:  () => appStore.dispatch(addContentFavoriteAction(action.contentId, action.contentType))
  });
}
